import React from 'react';
import get from 'lodash/get';
import Button, { LoadingButton } from '@atlaskit/button';
import Toggle from '@atlaskit/toggle';
import {
  analytics,
  AnalyticsButtonLabels,
  AnalyticsTrackAction,
  AnalyticsTrackSubject,
  trackButtonClick,
} from '@atlassian/temp-analytics';
import { tableErrorMessage } from '@css-xp/ppc-common';
import { Estimate } from './estimate';
import { useProcessLineItemsForOrderIntentEstimate } from './hooks/use-process-line-items-for-order-intent-estimate';
import { CheckoutOperation, usePartnerCheckoutNotification } from './hooks/use-place-order-notification';
import { formatLineItems } from './utils';
import { PartnerQuoteLineItems } from './types';
import { usePartnerCatalogueContext } from './context/partner-catalogue-context';
import './pre-checkout-summary.scss';
import { useFeatureToggle } from '@css-xp/base';
import { STATSIG_FEATURE_GATES } from '@css-xp/ppc-constants';

export type PreCheckoutSummaryProps = {
  transactionAccountId: string;
  applicablePromotionId: string;
  onCheckout: (_lineItems: PartnerQuoteLineItems) => void;
};

export const PreCheckoutSummary: React.FC<PreCheckoutSummaryProps> = ({ transactionAccountId }) => {
  const {
    quoteItems: itemsInCart,
    clearCart,
    quoteFormData,
    isPartnerCatalogueInformationLoading,
    getSelectedPricingPlanForAProduct,
    estimatesToBeRefreshed,
  } = usePartnerCatalogueContext();
  const { notifyFailure } = usePartnerCheckoutNotification();

  const [areEstimatesBeingCalculated, setAreEstimatesBeingCalculated] = React.useState(false);

  const [isEnabled] = useFeatureToggle();
  const isJPDAsJiraFamilyEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_JPD_AS_JIRA_FAMILY);

  const lineItems = formatLineItems(quoteFormData, getSelectedPricingPlanForAProduct, isJPDAsJiraFamilyEnabled);

  const {
    processLineItemsForOrderEstimate,
    getEstimate,
    orderIntentId,
    hasBillEstimateError,
    totalAmountToPay,
  } = useProcessLineItemsForOrderIntentEstimate({
    lineItemsData: lineItems,
  });

  if (!(itemsInCart?.length > 0)) {
    return null;
  }

  const getTotalSelectedLineItems = () => {
    const numberOfItemsInTheCart = get(itemsInCart, 'length');
    return numberOfItemsInTheCart + ' ' + (numberOfItemsInTheCart > 1 ? 'PRODUCTS' : 'PRODUCT') + ' SELECTED';
  };

  return (
    <div className="catalogue-quote-summary" data-testid="ppc-pre-checkout-summary">
      <div className="row-container">
        <label className={'number-of-products'}>{getTotalSelectedLineItems()}</label>
        <Button className={'reset-button'} appearance="subtle-link" testId="reset-cat-quote" onClick={clearCart}>
          Reset
        </Button>
      </div>
      <div className="row-container">
        <div className="pre-checkout-billing-cycle">
          <p>
            <u>
              <b>Monthly</b>
            </u>
          </p>
          <div className="custom-toggle">
            <Toggle />
          </div>
          <p>Annual</p>
        </div>
        <LoadingButton
          className={'calculate-estimate'}
          appearance="default"
          testId="calculate-estimate-quote"
          isLoading={areEstimatesBeingCalculated}
          isDisabled={isPartnerCatalogueInformationLoading}
          onClick={async () => {
            try {
              setAreEstimatesBeingCalculated(true);
              const map = undefined;
              // Skipping the calls to get promotions in pre-checkout summary
              // if (!areAllTheAddedProductsFree) {
              //   map = await getPromotions();
              // }
              await getEstimate({
                intentEstimateRequestPayload: {
                  transactionAccountId,
                  orderIntentId,
                  items: processLineItemsForOrderEstimate(lineItems, map),
                },
                lineItems,
              });
            } catch {
              notifyFailure(tableErrorMessage(), CheckoutOperation.CalculateEstimate);
            } finally {
              setAreEstimatesBeingCalculated(false);
            }
            lineItems.forEach((quoteItem) => {
              analytics.sendTrackEvent({
                attributes: {
                  product: quoteItem.name,
                  numberOfUser: quoteItem.estimatedNumberOfUsers,
                },
                action: AnalyticsTrackAction.CALCULATED,
                actionSubject: AnalyticsTrackSubject.BILL_ESTIMATE,
              });
            });
            trackButtonClick(AnalyticsButtonLabels.CALCULATE_BILL_ESTIMATE);
          }}
        >
          Calculate estimate
        </LoadingButton>
        <span>
          {hasBillEstimateError || estimatesToBeRefreshed ? (
            <h4 data-testId="total-estimate-fails" className="total-estimate-fails">
              N/A
            </h4>
          ) : (
            <Estimate
              currency={'USD'}
              price={totalAmountToPay ?? 0}
              isTotal={true}
              className={
                areEstimatesBeingCalculated || isPartnerCatalogueInformationLoading ? 'total-amount-disabled' : ''
              }
            />
          )}
          <p className="currency-subtitle">Excl. tax and discounts</p>
        </span>
      </div>
      <div className="row-container"></div>
    </div>
  );
};
